<script setup lang="ts">
/**
 * The layer's elements, front first, to pick and to restack.
 *
 * Restacked by dragging: a row is taken by its grip (or anywhere on it) and
 * dropped where the line shows, between two others; with several picked,
 * dragging one of them moves them all, in their order. Near the top or the
 * foot of the panel the list scrolls on its own, so an element can be taken
 * from the back of a long list to the front of it in one gesture. The arrows
 * on each row do the same a step at a time, from the keyboard too.
 *
 * Only says what was asked: the layer, which owns the elements, restacks them.
 */
import { useI18n } from 'vue-i18n'

import { ref, useTemplateRef } from 'vue'

/** One row: an element's id, what kind it is and what it reads. */
interface LayerRow {
  readonly id: string
  readonly kind: string
  readonly label: string
}

const props = defineProps<{
  /** Front first, as the page shows them. */
  rows: readonly LayerRow[]
  selected: readonly string[]
}>()

const emit = defineEmits<{
  pick: [id: string, event: MouseEvent]
  forward: [id: string]
  backward: [id: string]
  duplicate: [id: string]
  remove: [id: string]
  /** Move these, together, before the row at `gap` as the list stands, or to its end. */
  restack: [moving: string[], gap: number]
}>()

const { t } = useI18n()

const list = useTemplateRef<HTMLElement>('list')

/** The rows being dragged, while they are. */
const dragging = ref<readonly string[] | null>(null)
/** Where they would land: before the row at this place, or after the last. */
const gap = ref<number | null>(null)

/** How near the edge of the panel the pointer has to be for the list to scroll, in pixels. */
const EDGE_PX = 56

function start(row: LayerRow, event: DragEvent): void {
  const together = props.selected.length > 1 && props.selected.includes(row.id)
  dragging.value = together
    ? props.rows.filter((each) => props.selected.includes(each.id)).map((each) => each.id)
    : [row.id]
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', row.id)
  }
}

function over(at: number, event: DragEvent): void {
  if (!dragging.value) return
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  const box = (event.currentTarget as HTMLElement).getBoundingClientRect()
  gap.value = event.clientY < box.top + box.height / 2 ? at : at + 1
  follow(event)
}

/** Past the last row, the end of the list. */
function overEnd(event: DragEvent): void {
  if (!dragging.value || event.target !== event.currentTarget) return
  event.preventDefault()
  gap.value = props.rows.length
  follow(event)
}

function drop(event: DragEvent): void {
  event.preventDefault()
  if (dragging.value && gap.value !== null) emit('restack', [...dragging.value], gap.value)
  end()
}

function end(): void {
  dragging.value = null
  gap.value = null
}

/** The panel the list scrolls in. */
function scroller(): HTMLElement | null {
  for (let at = list.value?.parentElement ?? null; at; at = at.parentElement) {
    const { overflowY } = getComputedStyle(at)
    if ((overflowY === 'auto' || overflowY === 'scroll') && at.scrollHeight > at.clientHeight) {
      return at
    }
  }
  return null
}

/**
 * The list scrolled under a drag held near the edge of the panel, faster the
 * nearer it is. The top edge is under the tabs pinned there, not the panel's.
 */
function follow(event: DragEvent): void {
  const panel = scroller()
  if (!panel) return
  const box = panel.getBoundingClientRect()
  const pinned = panel.querySelector('[role="tablist"]')?.getBoundingClientRect().bottom
  const top = Math.max(box.top, pinned ?? box.top)
  if (event.clientY < top + EDGE_PX) {
    panel.scrollTop -= Math.ceil((top + EDGE_PX - event.clientY) / 3)
  } else if (event.clientY > box.bottom - EDGE_PX) {
    panel.scrollTop += Math.ceil((event.clientY - (box.bottom - EDGE_PX)) / 3)
  }
}

function moving(id: string): boolean {
  return dragging.value?.includes(id) ?? false
}
</script>

<template>
  <ul
    ref="list"
    class="rows"
    :class="{ 'rows--dragging': dragging }"
    @dragover="overEnd"
    @drop="drop"
  >
    <li
      v-for="(row, at) in rows"
      :key="row.id"
      class="row"
      :class="{
        'row--on': selected.includes(row.id),
        'row--moving': moving(row.id),
        'row--before': gap === at,
        'row--after': gap === rows.length && at === rows.length - 1,
      }"
      draggable="true"
      :data-row="row.id"
      @dragstart="start(row, $event)"
      @dragover="over(at, $event)"
      @drop="drop"
      @dragend="end"
    >
      <span class="row__grip" :title="t('inspector.layer.drag')" aria-hidden="true">
        <svg width="10" height="14" viewBox="0 0 10 14">
          <circle cx="3" cy="3" r="1.1" />
          <circle cx="7" cy="3" r="1.1" />
          <circle cx="3" cy="7" r="1.1" />
          <circle cx="7" cy="7" r="1.1" />
          <circle cx="3" cy="11" r="1.1" />
          <circle cx="7" cy="11" r="1.1" />
        </svg>
      </span>
      <button type="button" class="row__pick" @click="emit('pick', row.id, $event)">
        <span class="row__kind">{{ row.kind }}</span>
        <span class="row__label">{{ row.label }}</span>
      </button>
      <span class="row__ops">
        <button
          type="button"
          class="b-icon-btn"
          :title="t('inspector.layer.forward')"
          :aria-label="t('inspector.layer.forward')"
          @click="emit('forward', row.id)"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M8 12.5v-9M4.5 7l3.5-3.5L11.5 7"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          class="b-icon-btn"
          :title="t('inspector.layer.backward')"
          :aria-label="t('inspector.layer.backward')"
          @click="emit('backward', row.id)"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M8 3.5v9M4.5 9l3.5 3.5L11.5 9"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          class="b-icon-btn"
          :title="t('inspector.layer.duplicate')"
          :aria-label="t('inspector.layer.duplicate')"
          @click="emit('duplicate', row.id)"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M5.5 5.5h8v8h-8z M2.5 10.5v-8h8"
              fill="none"
              stroke="currentColor"
              stroke-width="1.25"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          class="b-icon-btn b-btn--danger"
          :title="t('inspector.layer.remove')"
          :aria-label="t('inspector.layer.remove')"
          @click="emit('remove', row.id)"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M4 4l8 8M12 4l-8 8"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </span>
    </li>
  </ul>
</template>

<style scoped>
.rows {
  margin: 0;
  padding: 0 0 var(--sp-4);
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.row {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  border-radius: var(--radius);
  user-select: none;
  transition:
    background var(--quick),
    opacity var(--quick);
}

.row:hover {
  background: var(--panel-hover);
}

.row--on {
  background: var(--accent-quiet);
}

/* Taken up: faded where it was, so the line shows where it goes. */
.row--moving {
  opacity: 0.4;
}

/* Where the rows would land: a line in the accent, between two rows. */
.row--before::before,
.row--after::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  border-radius: 2px;
  background: var(--accent-fill);
  pointer-events: none;
}

.row--before::before {
  top: -2px;
}

.row--after::after {
  bottom: -2px;
}

.row__grip {
  flex: none;
  display: grid;
  place-items: center;
  width: 16px;
  height: 26px;
  margin-left: 2px;
  color: var(--ink-faint);
  fill: currentColor;
  cursor: grab;
  opacity: 0.55;
}

.row:hover .row__grip,
.row--on .row__grip {
  opacity: 1;
}

.rows--dragging .row,
.rows--dragging .row__grip {
  cursor: grabbing;
}

.row__pick {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: var(--sp-3);
  padding: var(--sp-2) var(--sp-2);
  border: 0;
  background: none;
  color: var(--ink);
  font: inherit;
  font-size: var(--text-sm);
  text-align: left;
  cursor: pointer;
}

.row__kind {
  flex: none;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.row__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row__ops {
  flex: none;
  display: flex;
  gap: 1px;
  opacity: 0.55;
  transition: opacity var(--quick);
}

.row:hover .row__ops,
.row--on .row__ops {
  opacity: 1;
}
</style>
